import { defineAsyncComponent } from 'vue'
import { tabOutlineIdBadge2 } from 'quasar-extras-svg-icons/tabler-icons-v3'
import type { QueryConfig } from 'src/composables/use-query'
import type {
  FeatureConfig,
  MainFeatureTwin,
  FeatureQueryTwin,
  FeatureParameterConfigs
} from 'src/models'
import { CardSelection } from 'src/components/tiles/CardsTile'
import type { CardAction } from 'src/components/tiles/CardsTile/CardActions.vue'
import type { ButtonStyle } from 'src/components/features/DashboardFeature'

export interface CardsFeatureTwin extends MainFeatureTwin, QueryConfig {
  cardWidth?: number
  cardImgHeight?: number
  loadDrawerFeature?: string
  filter?: boolean
  allowExportExcel?: boolean
  /** Navigation mode for cards layout */
  navigation?: 'scroll' | 'carousel' | 'pagination' | 'dynamicAction'
  /** Enable realtime updates (only for ADT queries that return full twins) */
  realtime?: boolean
  /** Configure which card properties should update in realtime from the store */
  realtimeItemProperties?: {
    [propertyKey: string]: {
      propertyName: string
      twinIdColumn: string
      twinProperty: string
    }
  }
  viewerIsolateItems?: boolean
  itemItems?: {
    [itemNo: string]: {
      itemLabel: string
      itemSubLabel?: string
      itemValue?: string
      itemColor?: string
    }
  }
  itemCustomActions?: {
    [itemNo: string]: {
      icon: string
      label: string
      loadFeatureId: string
    }
  }
  parameters?: FeatureParameterConfigs
  /** Selection type for cards */
  selection?: CardSelection
  /** Actions configuration for card items */
  actions?: { [key: string]: CardAction }
  /** How to display actions: 'buttons' or 'contextMenu' */
  actionsDisplayStyle?: 'buttons' | 'contextMenu'
  /** Style configuration for action buttons */
  actionsButtonStyle?: {
    style?: ButtonStyle
    outline?: boolean
    flat?: boolean
    cssStyle?: string
    cssClass?: string
  }
  /** HTML content with JEXL evaluation */
  content?: string
  /** Show only the content passed via prop */
  contentOnly?: boolean
  /** Automatically sync selected twins to the store when selection changes */
  syncSelection?: boolean
  /** Always fetch selected twins from API even if already in the store */
  syncSelectionOverwrite?: boolean
}

/** dtmi:com:arcadis:app:ListQuery;2 */
export interface CardsQueryTwin extends FeatureQueryTwin, QueryConfig {
  allowCreate?: boolean
  createName?: string
  createModelId?: string
  createParentTwinId?: string
  createRelationshipName?: string
  /** Enable realtime updates (only for ADT queries that return full twins) */
  realtime?: boolean
  /** Configure which card properties should update in realtime from the store */
  realtimeItemProperties?: {
    [propertyKey: string]: {
      propertyName: string
      twinIdColumn: string
      twinProperty: string
    }
  }
  /** Navigation mode for cards layout */
  navigation?: 'scroll' | 'carousel' | 'pagination' | 'dynamicAction'
  viewerLoadState?: boolean
  viewerIsolateItems?: boolean
  viewerIsolateChildItems?: boolean
  viewerShowChildItems?: boolean
  viewerShowParentItems?: boolean
  viewerColorItems?: boolean
  viewerColorChildItems?: boolean
  viewerColorParentItems?: boolean
  viewerLabelItems?: boolean
  viewerHideMarkers?: boolean
  itemItems?: {
    [itemNo: string]: {
      itemLabel: string
      itemSubLabel?: string
      itemValue?: string
      itemColor?: string
    }
  }
  itemCustomActions?: {
    [itemNo: string]: {
      icon: string
      label: string
      loadFeatureId: string
    }
  }
  /** Selection type for cards: 'none' | 'single' | 'multiple' */
  selection?: CardSelection
  /** Actions configuration for card items */
  actions?: { [key: string]: CardAction }
  /** How to display actions: 'buttons' or 'contextMenu' */
  actionsDisplayStyle?: 'buttons' | 'contextMenu'
  /** Style configuration for action buttons */
  actionsButtonStyle?: {
    style?: ButtonStyle
    outline?: boolean
    flat?: boolean
    cssStyle?: string
    cssClass?: string
  }
  /** HTML content with JEXL evaluation */
  content?: string
  /** Show only the content passed via prop */
  contentOnly?: boolean
}

const featureConfig: FeatureConfig = {
  main: {
    mainComponent: defineAsyncComponent(() => import('./CardsFeatureMain.vue')),
    splitter: 450,
    icon: tabOutlineIdBadge2,
    label: 'Cards'
  }
}

export default featureConfig

// Re-export for convenience
export { CardSelection }
